import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Route, Switch, useHistory, useParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { v4 as uuid } from 'uuid';
import Loader from '../../../components/Loader';
import SingleColGrid from '../../../components/SingleColGrid';
import { ROUTES } from '../../../routes/routes';
import { GatedContentConfiguration } from '../../../types/gatedContent';
import { useAxios } from '../../../utils/hooks';
import { GatedContentHeader } from '../GatedContentHeader';
import GatedContentForm from './GatedContentForm';
import ViewSnippet from './ViewSnippet';
import { isGSheet } from '../../../common/gatedContentUtils';

export default function NewGatedContent() {
    const { id: gatedContentId } = useParams<{ id: string }>();
    const { addToast } = useToasts();
    const axios = useAxios();
    const history = useHistory();

    const [gatedContent, setGatedContent] = useState<GatedContentConfiguration>();

    useEffect(() => {
        if (!axios.current) {
            return;
        }
        if (gatedContentId) {
            axios.current
                ?.get<GatedContentConfiguration>(`/gated-content/${gatedContentId}`)
                .then((res) => {
                    setGatedContent(res.data);
                })
                .catch((err) =>
                    addToast('Something went wrong. Please reload the page and try again', { appearance: 'error' })
                );
        } else {
            setGatedContent({
                id: uuid(),
                name: '',
                gatedDomains: [''],
                gatedPaths: [{ path: '', matchType: 'WILDCARD' }],
                authFailureRedirectUrl: '',
            });
        }
    }, [axios, addToast, gatedContentId]);

    const sendUpdateRequest = useCallback(
        debounce(
            async (newConfiguration: GatedContentConfiguration) =>
                axios.current
                    ?.post<GatedContentConfiguration>('/gated-content', newConfiguration)
                    .catch((err) =>
                        addToast('Something went wrong. Please reload the page and try again.', { appearance: 'error' })
                    ),
            1000
        ),
        [axios, gatedContent, addToast]
    );

    const handleChange = (data: GatedContentConfiguration) => {
        setGatedContent(data);
        sendUpdateRequest(data);
    };

    const Form = function () {
        if (!gatedContent) {
            return <Loader full />;
        }
        return <GatedContentForm onChange={handleChange} gatedContent={gatedContent} />;
    };

    const EditForm = function () {
        if (!gatedContent) {
            return <Loader full />;
        }
        if (isGSheet(gatedContent)) {
            history.push(ROUTES.GATED_CONTENT_SHEETS_EDIT.replace(':id', gatedContent.id));
        }
        return <GatedContentForm onChange={handleChange} gatedContent={gatedContent} />;
    };

    const Snippet = function () {
        if (!gatedContent) {
            return <Loader full />;
        }
        return <ViewSnippet gatedContent={gatedContent} />;
    };

    return (
        <div className="flex flex-col w-full">
            <GatedContentHeader subtitle="Create new Gated Content" />
            <SingleColGrid className="flex-1">
                <Switch>
                    <Route exact path={ROUTES.GATED_CONTENT_EDIT} component={EditForm} />
                    <Route exact path={ROUTES.GATED_CONTENT_NEW} component={Form} />
                    <Route exact path={ROUTES.GATED_CONTENT_SNIPPET} component={Snippet} />
                </Switch>
            </SingleColGrid>
        </div>
    );
}
